/**
 * TraderSettingsPanel — shown in CopyPortfolioPage when the connected wallet
 * is a registered leader trader. Lets the trader update fee/cap and claim fees.
 */
import { Trans, t } from "@lingui/macro";
import { useCallback, useEffect, useState } from "react";
import { ethers } from "ethers";

import { getContract } from "config/contracts";
import { claimCopyFees, updateTraderSettings } from "domain/copyTrading/contracts";
import { useChainId } from "lib/chains";
import { usePendingTxns } from "lib/usePendingTxns";
import { useEthersSigner } from "lib/wallets/useEthersSigner";

import CopyTradingStorageAbi from "sdk/abis/CopyTradingStorage.json";

type TraderProfile = {
  copyFeeRate: number;
  maxFollowers: number;
  followerCount: number;
};

type Props = {
  account: string;
  profile: TraderProfile;
};

// Collateral tokens that can accumulate copy fees on BESC Hyperchain
const CLAIMABLE_TOKENS: Array<{ symbol: string; address: string }> = [
  { symbol: "BUSDC", address: "0xb54ad626e127f0f228dbeab6f2a61e8e6e029a4b" },
];

function usePendingFees(chainId: number, account: string, signer: ethers.Signer | undefined) {
  const [fees, setFees] = useState<Record<string, bigint>>({});

  useEffect(() => {
    if (!signer || !account) return;
    const addr = getContract(chainId, "CopyTradingStorage");
    const contract = new ethers.Contract(addr, CopyTradingStorageAbi, signer);

    Promise.all(
      CLAIMABLE_TOKENS.map(async (token) => {
        const pending: bigint = await contract.pendingFees(account, token.address).catch(() => 0n);
        return { symbol: token.symbol, address: token.address, amount: pending };
      })
    ).then((results) => {
      const map: Record<string, bigint> = {};
      results.forEach((r) => { if (r.amount > 0n) map[r.address] = r.amount; });
      setFees(map);
    });
  }, [chainId, account, signer]);

  return fees;
}

export function TraderSettingsPanel({ account, profile }: Props) {
  const { chainId } = useChainId();
  const signer = useEthersSigner({ chainId });
  const [, setPendingTxns] = usePendingTxns();

  const [editMode, setEditMode] = useState(false);
  const [feeInput, setFeeInput] = useState(String((profile.copyFeeRate / 100).toFixed(1)));
  const [maxFollowersInput, setMaxFollowersInput] = useState(String(profile.maxFollowers));
  const [isSaving, setIsSaving] = useState(false);
  const [isClaiming, setIsClaiming] = useState<string | null>(null);

  const pendingFees = usePendingFees(chainId, account, signer);
  const hasAnyFees = Object.keys(pendingFees).length > 0;

  const handleSaveSettings = useCallback(async () => {
    if (!signer) return;
    const feeBps = Math.round(parseFloat(feeInput) * 100);
    const maxFollowers = parseInt(maxFollowersInput) || 0;
    if (feeBps < 0 || feeBps > 1000) return;

    setIsSaving(true);
    try {
      await updateTraderSettings({ chainId, signer, copyFeeRateBps: feeBps, maxFollowers, setPendingTxns });
      setEditMode(false);
    } catch {
      // callContract shows error toast
    } finally {
      setIsSaving(false);
    }
  }, [signer, chainId, feeInput, maxFollowersInput, setPendingTxns]);

  const handleClaimFees = useCallback(async (tokenAddress: string) => {
    if (!signer) return;
    setIsClaiming(tokenAddress);
    try {
      await claimCopyFees({ chainId, signer, tokenAddress, setPendingTxns });
    } catch {
      // callContract shows error toast
    } finally {
      setIsClaiming(null);
    }
  }, [signer, chainId, setPendingTxns]);

  return (
    <div className="CT-trader-panel">
      <div className="CT-trader-panel__header">
        <div>
          <h3 className="CT-trader-panel__title"><Trans>Leader Dashboard</Trans></h3>
          <p className="CT-trader-panel__sub">
            {profile.followerCount} <Trans>follower{profile.followerCount !== 1 ? "s" : ""} copying you</Trans>
          </p>
        </div>
        {!editMode && (
          <button className="CT-btn-secondary" onClick={() => setEditMode(true)}>
            <Trans>Edit Settings</Trans>
          </button>
        )}
      </div>

      {/* Current / edit settings */}
      {editMode ? (
        <div className="CT-trader-panel__edit">
          <div className="CT-modal-field">
            <label className="CT-modal-field__label"><Trans>Copy fee (%)</Trans></label>
            <div className="CT-modal-input-wrap">
              <input
                className="CT-modal-input"
                type="number" min="0" max="10" step="0.1"
                value={feeInput}
                onChange={(e) => setFeeInput(e.target.value)}
              />
              <span className="CT-modal-input-token">%</span>
            </div>
          </div>
          <div className="CT-modal-field">
            <label className="CT-modal-field__label">
              <Trans>Max followers</Trans>{" "}
              <span className="CT-modal-optional">({t`0 = unlimited`})</span>
            </label>
            <div className="CT-modal-input-wrap">
              <input
                className="CT-modal-input"
                type="number" min="0" step="1"
                value={maxFollowersInput}
                onChange={(e) => setMaxFollowersInput(e.target.value)}
              />
            </div>
          </div>
          <div className="CT-trader-panel__edit-actions">
            <button
              className="CT-btn-primary"
              onClick={handleSaveSettings}
              disabled={isSaving}
            >
              {isSaving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </button>
            <button className="CT-btn-secondary" onClick={() => setEditMode(false)}>
              <Trans>Cancel</Trans>
            </button>
          </div>
        </div>
      ) : (
        <div className="CT-trader-panel__stats">
          <div className="CT-portfolio-stat">
            <span className="CT-portfolio-stat__label"><Trans>Copy fee</Trans></span>
            <span className="CT-portfolio-stat__value">
              {profile.copyFeeRate === 0 ? t`Free` : `${(profile.copyFeeRate / 100).toFixed(1)}%`}
            </span>
          </div>
          <div className="CT-portfolio-stat">
            <span className="CT-portfolio-stat__label"><Trans>Max followers</Trans></span>
            <span className="CT-portfolio-stat__value">
              {profile.maxFollowers === 0 ? t`Unlimited` : profile.maxFollowers}
            </span>
          </div>
          <div className="CT-portfolio-stat">
            <span className="CT-portfolio-stat__label"><Trans>Current followers</Trans></span>
            <span className="CT-portfolio-stat__value">{profile.followerCount}</span>
          </div>
        </div>
      )}

      {/* Claimable fees */}
      <div className="CT-trader-panel__fees">
        <div className="CT-trader-panel__fees-title">
          <Trans>Claimable Fees</Trans>
        </div>
        {hasAnyFees ? (
          <div className="CT-trader-panel__fee-rows">
            {CLAIMABLE_TOKENS.filter((t) => pendingFees[t.address] !== undefined).map((token) => {
              const amount = pendingFees[token.address];
              const usdcAmount = (Number(amount) / 1e6).toFixed(4);
              return (
                <div key={token.address} className="CT-trader-panel__fee-row">
                  <span>
                    <strong>{usdcAmount}</strong> {token.symbol}
                  </span>
                  <button
                    className="CT-btn-follow"
                    disabled={isClaiming === token.address}
                    onClick={() => handleClaimFees(token.address)}
                  >
                    {isClaiming === token.address ? <Trans>Claiming…</Trans> : <Trans>Claim</Trans>}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="CT-muted" style={{ fontSize: "var(--font-size-body-small)", margin: 0 }}>
            <Trans>No fees to claim yet. Fees accumulate as your followers trade.</Trans>
          </p>
        )}
      </div>
    </div>
  );
}
